import { useMsal } from '@azure/msal-react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChatBubbleLeftRightIcon, Cog8ToothIcon, MoonIcon, SunIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';
import { AuthHelper } from '../../../libs/auth/AuthHelper';
import { useToken } from '../../../libs/hooks/useToken';
import { useChat } from '../../../libs/hooks/useChat';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { resetState, RootState } from '../../../redux/app/store';
import { setConversationDisplayMode } from '../../../redux/features/app/appSlice';
import { IConversationDisplayMode } from '../../../redux/features/app/AppState';
import { setInitialChatsLoaded, setIsLoadingChats } from '../../../redux/features/conversations/conversationsSlice';
import { NewChatMenu } from '../ChatView/ChatList/NewChatMenu';
import { IAppBarProps } from './IAppBarProps';
import { UserProfile } from './UserProfile';

export const AppBar: React.FC<IAppBarProps> = ({
    isDarkMode,
    isSettingsOpen,
    isChatListOpen,
    toggleDarkMode,
    toggleChatList,
    toggleSettings,
    setLoadingState,
}) => {
    const { instance } = useMsal();
    const token = useToken();
    const chat = useChat();
    const dispatch = useAppDispatch();
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const { conversations, initialChatsLoaded } = useAppSelector((state: RootState) => state.conversations);

    const onAddChat = () => {
        dispatch(setConversationDisplayMode(IConversationDisplayMode.Active));
        void chat.createChat();
    };

    const onLogout = useCallback(() => {
        setLoadingState();
        token.revokeToken().then(() => {
            AuthHelper.logoutAsync(instance);
            resetState();
        });
    }, [instance, token, setLoadingState]);

    const loadAllChats = () => {
        if (!isChatListOpen && !initialChatsLoaded) {
            dispatch(setIsLoadingChats(true));
            void chat.loadChats(conversations).then(() => {
                dispatch(setIsLoadingChats(false));
                dispatch(setInitialChatsLoaded(true));
            });
        }
        toggleChatList();
    };

    return (
        <div className="flex duration-300 border-t z-50 w-full h-12 inset-x-0 mx-auto border-slate-300 bg-slate-50 border-base-50 dark:border-slate-700 dark:bg-slate-900 sm:relative sm:flex sm:h-screen sm:w-[60px] sm:min-w-[60px] sm:border-r sm:border-t-0">
            <div className="flex flex-row flex-1 sm:space-y-4 sm:flex-col justify-evenly sm:justify-between items-center h-full sm:py-3">
                <NewChatMenu appbar newChat={onAddChat} />
                <a
                    href="#"
                    title="View previous chats"
                    onClick={() => loadAllChats()}
                    className={`rounded-lg p-1 sm:p-1.5 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800 ${isChatListOpen ? 'bg-slate-200 dark:bg-slate-700' : ''}`}>
                    <ChatBubbleLeftRightIcon className="h-6 w-6" strokeWidth={2} />
                </a>
                {/* <a
                    href="#"
                    title="Request for Proposal"
                    className="rounded-lg p-1 sm:p-1.5 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800">
                    <BriefcaseIcon className="h-6 w-6" strokeWidth={2} />
                </a> */}
                <div className="hidden sm:flex-grow sm:inline sm:invisible"></div>
                {/* Bottom app bar */}
                <div className="rounded-lg p-1 sm:p-1.5 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800">
                    <Popover>
                        <PopoverButton className="align-middle">
                            <a href="#" title="Profile">
                                <UserCircleIcon className="h-6 w-6" strokeWidth={2} />
                            </a>
                        </PopoverButton>
                        <PopoverPanel
                            transition
                            anchor="left end"
                            className="divide-y divide-white/5 rounded-xl text-slate-700 bg-white dark:bg-slate-600 shadow-md text-sm/6 transition duration-200 ease-in-out [--anchor-gap:var(--spacing-5)] data-[closed]:-translate-y-1 data-[closed]:opacity-0 z-[200]">
                            <div className="sm:flex sm:flex-grow p-3 overflow-hidden overflow-ellipsis whitespace-nowrap">
                                <a
                                    className="flex truncate items-center rounded-lg py-1 px-1 transition hover:bg-slate-200 dark:hover:bg-slate-600 dark:text-slate-200"
                                    href="#">
                                    <UserProfile activeUserInfo={activeUserInfo} features={features} />
                                </a>
                                <a
                                    onClick={onLogout}
                                    className="flex items-center rounded-lg py-1 px-1 transition hover:bg-slate-200 dark:hover:bg-slate-600 dark:text-slate-200"
                                    href="#">
                                    <p className="w-full sm:w-auto text-center sm:ml-2 mr-2 bg-slate-400 dark:bg-slate-500 p-2 rounded-md text-slate-50">
                                        Sign out
                                    </p>
                                </a>
                            </div>
                        </PopoverPanel>
                    </Popover>
                </div>
                <a
                    href="#"
                    title="Settings"
                    onClick={() => toggleSettings()}
                    className={`rounded-lg p-1 sm:p-1.5 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800 ${isSettingsOpen ? 'bg-slate-200 dark:bg-slate-700' : ''}`}>
                    <Cog8ToothIcon className="h-6 w-6" strokeWidth={2} />
                </a>
                {/* Darkmode */}
                <a
                    href="#"
                    title={`Toggle ${isDarkMode ? 'Light' : 'Dark'} Mode`}
                    onClick={() => toggleDarkMode()}
                    className={`rounded-lg p-1 sm:p-1.5 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800`}>
                    {isDarkMode ? (
                        <SunIcon className="h-6 w-6" strokeWidth={2} />
                    ) : (
                        <MoonIcon className="h-6 w-6" strokeWidth={2} />
                    )}
                </a>
            </div>
        </div>
    );
};
